import { Box, Button, Typography, Paper, IconButton, Tooltip, Stack, Chip, CircularProgress } from "@mui/material";
import { DownloadIcon } from "lucide-react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import type { DocTemplateFormData, DocTemplateListItem } from "./templateSerializer";

const previewBoxStyles = {
  p: "12px 16px",
  "& > p, & > ul, & > ol, & > blockquote, & > pre, & > h1, & > h2, & > h3, & > h4, & > h5, & > h6": {
    lineHeight: 2.5,
  },
  "& > p": { margin: "0 0 12px" },
  "& table td p, & table th p": {
    margin: 0,
    marginBottom: 0,
    lineHeight: "inherit",
  },
  // Descendant (not direct-child) selector — a <ul> nested inside an <li> is not a direct
  // child of the preview root, so "& > ul" alone never matches it, leaving sub-bullets with
  // no indentation at all.
  "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 8px" },
  "& table td ul, & table td ol, & table th ul, & table th ol": {
    margin: 0,
    marginBottom: 0,
  },
  "& a": { color: "#1976d2", textDecoration: "underline" },
  "& u": { textDecoration: "underline !important" },
  "& s": { textDecoration: "line-through !important" },
  "& img": {
    maxWidth: "100%",
    verticalAlign: "bottom",
  },
  "& table": { borderCollapse: "collapse", tableLayout: "auto", margin: 0 },
  "& table td, & table th": {
    minWidth: "1em",
    border: "1px solid #ddd",
    padding: "4px 8px",
    verticalAlign: "top",
    boxSizing: "border-box",
  },
};

// Placeholder styles used for empty PPT preview states
const placeholderBoxStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  minHeight: "300px",
  color: "text.secondary",
  p: 2,
  textAlign: "center",
  borderRadius: 2.5,
  bgcolor: "#f9f9f9",
  border: "1px dashed #ccc",
};

interface TemplatePreviewPanelProps {
  templateListCollapsed: boolean;
  editorWidth: number;
  isDragging: boolean;
  isReadOnlyMode: boolean;
  templateData: DocTemplateFormData;
  editorMarkdown: string;
  previewHtml: string;
  previewContainerRef: React.RefObject<HTMLDivElement>;
  isPpt: boolean;
  hasLocalPptFile: boolean;
  isExistingPpt: boolean;
  pptUrl: string | null;
  powerPointFile: File | null;
  selectedIndex: number | null;
  list: DocTemplateListItem[];
  isGlobalAdmin: boolean;
  loading: boolean;
  onSave: () => void;
}

const TemplatePreviewPanel = ({
  templateListCollapsed,
  editorWidth,
  isDragging,
  isReadOnlyMode,
  templateData,
  editorMarkdown,
  previewHtml,
  previewContainerRef,
  isPpt,
  hasLocalPptFile,
  isExistingPpt,
  pptUrl,
  powerPointFile,
  selectedIndex,
  list,
  isGlobalAdmin,
  loading,
  onSave,
}: TemplatePreviewPanelProps) => {
  const editingOthersGlobalTemplate =
    selectedIndex !== null &&
    !isGlobalAdmin &&
    list[selectedIndex]?.templateUploadedBy === "global admin";

  return (
    <Paper
      className="tour-template-preview"
      elevation={3}
      sx={{
        borderRadius: 2.5,
        width: {
          xs: "100%",
          md: templateListCollapsed
            ? `calc(${100 - editorWidth}% - 8px)`
            : `calc(${100 - editorWidth}% - 8px)`,
        },
        minWidth: {
          xs: "100%",
          md: "300px",
        },
        flex: {
          xs: "none",
          md: "1 1 auto",
        },
        p: { xs: 1.5, lg: 2 },
        height: {
          xs: "auto",
          sm: "auto",
          md: "100%",
        },
        minHeight: {
          xs: "500px",
          md: "100%",
        },
        maxHeight: {
          xs: "none",
          md: "100%",
        },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: isDragging || !templateListCollapsed ? "none" : "width 0.3s",
        pb: { xs: 2, lg: 0 },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} sx={{ flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1rem", lg: "1.25rem" },
            }}
          >
            Preview
          </Typography>
          {isReadOnlyMode && (
            <Chip
              label="Read Only"
              size="small"
              color="info"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Tooltip
            title={
              templateData.templateType === "powerpoint"
                ? "Download not available for PowerPoint"
                : "Download Markdown"
            }
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={templateData.templateType === "powerpoint"}
                onClick={() => {
                  // Create a blob from the markdown content
                  const blob = new Blob([editorMarkdown], {
                    type: "text/markdown",
                  });

                  // Create a temporary download link
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;

                  // Use template name or default name
                  const fileName = templateData.templateName
                    ? `${templateData.templateName}.md`
                    : "template.md";
                  link.download = fileName;

                  // Trigger download
                  document.body.appendChild(link);
                  link.click();

                  // Cleanup
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }}
                sx={{
                  display: { xs: "none", sm: "inline-flex" }, // Hide on very small screens
                }}
              >
                <DownloadIcon
                  style={{
                    width: "26px",
                    height: "26px",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Markdown Preview / PowerPoint Placeholder */}
      <Box
        ref={previewContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: "#fff",
          borderRadius: 2.5,
          overflowY: "auto",
          overflowX: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {isPpt ? (
          <>
            {hasLocalPptFile ? (
              // 1. User has selected a new file
              <Box sx={placeholderBoxStyles}>
                <Stack spacing={2} alignItems="center">
                  <UploadFileIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                  <Typography fontWeight="bold">Preview will be available after saving</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {powerPointFile.name}
                  </Typography>
                </Stack>
              </Box>
            ) : isExistingPpt && pptUrl ? (
              // 2. User is viewing an existing, uploaded template with a valid URL
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(pptUrl)}`}
                width="100%"
                height="100%"
                frameBorder="0"
                title="PowerPoint Preview"
                style={{ minHeight: "400px", borderRadius: "8px" }}
              />
            ) : (
              // 3. New template (no file) or existing template (no URL)
              <Box sx={placeholderBoxStyles}>
                <Stack spacing={2} alignItems="center">
                  <InsertDriveFileIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                  <Typography fontWeight="bold">PowerPoint Preview</Typography>
                  <Typography variant="body2">
                    {selectedIndex === null
                      ? "Upload a file in the editor to create a new template"
                      : "No preview available for this template"}
                  </Typography>
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <Box className="template-preview" sx={previewBoxStyles} dangerouslySetInnerHTML={{ __html: previewHtml }} />
        )}
      </Box>
      <Stack
        direction="row"
        justifyContent={{ xs: "center", lg: "flex-end" }}
        mt={2}
        gap={2}
        sx={{ flexShrink: 0, pt: 1 }}
      >
        <Button
          variant="contained"
          onClick={onSave}
          className="tour-template-save-button"
          disabled={loading || editingOthersGlobalTemplate}
          sx={{
            minWidth: { xs: 120, lg: "auto" },
            fontSize: { xs: "0.875rem", lg: "1rem" },
            py: { xs: 1, lg: 1.5 },
            opacity: editingOthersGlobalTemplate ? 0.5 : 1,
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : selectedIndex !== null && list[selectedIndex]?.templateId ? (
            "Update"
          ) : (
            "Save"
          )}
        </Button>
      </Stack>
    </Paper>
  );
};

export default TemplatePreviewPanel;
